const asObject = (value) => (value && typeof value === 'object' ? value : {})

const formatDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatDate = (value) => {
  if (!value) return ''
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof value === 'string' ? value : '')
  if (match) return `${match[3]}-${match[2]}-${match[1]}`
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`
}

const money = (value) => {
  if (value === undefined || value === null || value === '') return ''
  const amount = Number(value)
  if (!Number.isFinite(amount)) return ''
  return `Rs. ${amount.toFixed(2)}`
}

const titleCase = (value) => {
  const text = String(value || '').trim()
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const userDetails = (booking) => {
  const user = asObject(booking.user)
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || ''
  return {
    name: name || 'Guest',
    email: user.email || '',
    phone: user.mobile_number || booking.alternate_phone_number || '',
    sangatId: user.handle_name || ''
  }
}

const paymentMethod = (booking) => {
  if (booking.razorpay_order_id) return 'Razorpay'
  if (booking.payment_transaction_id) return String(booking.payment_transaction_id)
  if (booking.payment_reference_id) return String(booking.payment_reference_id)
  return ''
}

const amountRows = (pairs) => pairs
  .filter((row) => row.value !== '')
  .map((row) => ({ label: row.label, value: row.value }))

const documentRows = (booking) => {
  const uploaded = Array.isArray(booking.uploaded_documents) ? booking.uploaded_documents : []
  return uploaded.map((document) => ({
    name: document.document_name || 'Document',
    status: titleCase(document.status || 'pending'),
    files: Array.isArray(document.file_urls) ? document.file_urls.length : 0
  }))
}

const baseModel = (booking, moduleLabel) => ({
  moduleLabel,
  bookingId: booking.booking_id || '',
  bookingDate: formatDateTime(booking.createdAt || booking.created_at),
  status: '',
  user: userDetails(booking),
  placeName: '',
  placeDetail: '',
  checkIn: '',
  checkOut: '',
  timeSlot: '',
  rooms: [],
  guests: '',
  paymentStatus: '',
  paymentMethod: paymentMethod(booking),
  amounts: [],
  refund: money(asObject(booking.cancellation_details).refund_amount),
  notes: booking.message || '',
  cancellation: asObject(booking.cancellation_details).cancellation_reason
    || asObject(booking.approval_details).rejection_reason
    || '',
  extras: [],
  documents: documentRows(booking)
})

const roomsOf = (booking) => (Array.isArray(booking.rooms) ? booking.rooms : []).map((room) => {
  const count = room.number_of_rooms || 1
  const name = room.room_name || room.room_type || 'Room'
  const price = money(room.price_per_night)
  return price ? `${count} x ${name} (${price} / night)` : `${count} x ${name}`
})

const guestsOf = (booking) => {
  const guests = asObject(booking.guests)
  const parts = []
  if (guests.adults) parts.push(`Adults ${guests.adults}`)
  if (guests.children) parts.push(`Children ${guests.children}`)
  if (guests.infants) parts.push(`Infants ${guests.infants}`)
  if (guests.pets) parts.push(`Pets ${guests.pets}`)
  return parts.join(', ')
}

export function normalizeStayBooking (booking, module) {
  const record = booking || {}
  const isHotel = module === 'hotel'
  const place = asObject(isHotel ? record.hotel : record.sarai)
  const model = baseModel(record, isHotel ? 'Hotel' : 'Sarai')
  const refunded = String(record.payment_status || '').toLowerCase() === 'refunded'
  model.status = refunded ? 'Refunded' : titleCase(record.status)
  model.paymentStatus = refunded ? 'Refunded' : titleCase(record.payment_status)
  model.placeName = place.name || ''
  model.placeDetail = [place.location, place.address, asObject(record.gurudwara).name].filter(Boolean).join(' · ')
  model.checkIn = formatDate(record.check_in_date)
  model.checkOut = formatDate(record.check_out_date)
  model.rooms = roomsOf(record)
  model.guests = guestsOf(record)
  model.amounts = amountRows([
    { label: 'Subtotal', value: money(record.subtotal) },
    { label: 'Taxes', value: money(record.taxes) },
    { label: 'Platform fee', value: money(record.platform_fee) },
    { label: 'Cancellation charge', value: money(asObject(record.cancellation_details).cancellation_charge) },
    { label: 'Total amount', value: money(record.total_amount) }
  ])
  if (record.service_location) model.extras.push({ label: 'Service location', value: record.service_location })
  if (record.number_of_nights) model.extras.push({ label: 'Nights', value: String(record.number_of_nights) })
  return model
}

export function normalizeServiceBooking (booking) {
  const record = booking || {}
  const service = asObject(record.gurudwara_service)
  const nested = asObject(service.service)
  const slot = asObject(record.booking_time_slot)
  const model = baseModel(record, 'Gurudwara Service')
  model.status = record.booking_status || ''
  model.paymentStatus = record.payment_status || ''
  model.placeName = nested.service_name || service.service_name || service.service_description || 'Service'
  model.placeDetail = asObject(record.gurudwara).name || ''
  model.checkIn = formatDate(record.booking_date)
  model.timeSlot = [slot.start_time, slot.end_time].filter(Boolean).join(' - ')
  model.amounts = amountRows([
    { label: 'Service amount', value: money(record.service_amount) },
    { label: 'Platform fee', value: money(record.platform_fee) },
    { label: 'Net receive', value: money(record.net_receive_amount) },
    { label: 'Cancellation charge', value: money(asObject(record.cancellation_details).cancellation_charge) },
    { label: 'Total amount', value: money(record.total_amount) }
  ])
  if (record.service_location) model.extras.push({ label: 'Service location', value: record.service_location })
  if (record.token_number) model.extras.push({ label: 'Token number', value: String(record.token_number) })
  if (record.token_status) model.extras.push({ label: 'Token status', value: record.token_status })
  if (record.prasad_status) model.extras.push({ label: 'Prasad status', value: record.prasad_status })
  const duty = Array.isArray(record.duty_assigned_to) ? record.duty_assigned_to : []
  const dutyNames = duty
    .map((person) => `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.handle_name)
    .filter(Boolean)
  if (dutyNames.length) model.extras.push({ label: 'Duty assigned', value: dutyNames.join(', ') })
  return model
}

export function normalizeBooking (booking, module) {
  if (module === 'service') return normalizeServiceBooking(booking)
  return normalizeStayBooking(booking, module)
}
